import { useMemo, useState, type ReactNode } from "react";

import type {
  ThreadV4NotebookEntry,
  ThreadV4NotebookEntryType,
  ThreadV4SpoolSourceType,
  ThreadV4SpoolVisibility,
  ThreadV4WidgetDock,
  ThreadV4WidgetSpec,
} from "../../api/threadV4Views";
import DAGPanel from "../../components/panels/DAGPanel";
import DataExplorerPanel from "../../components/panels/DataExplorerPanel";
import Flow360ViewerPanel from "../../components/panels/Flow360ViewerPanel";
import type { ThreadAsset, ThreadColumn, ThreadRow } from "../thread-design-system";
import type { V3WorkbenchData } from "../thread-v3/types";
import { Icons } from "./icons";
import { formatValue, rowKey } from "./threadV4RowUtils";

export { formatValue, rowKey } from "./threadV4RowUtils";

type WidgetState = "ok" | "stale" | "error" | "running";

interface WidgetShellProps {
  kind: string;
  kindLabel?: string;
  title: string;
  meta?: string;
  state?: WidgetState;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

interface NotebookControlProps {
  entries: ThreadV4NotebookEntry[];
  draft: string;
  type: ThreadV4NotebookEntryType;
  sourceType: ThreadV4SpoolSourceType;
  sourceUri: string;
  visibility: ThreadV4SpoolVisibility;
  onDraftChange: (value: string) => void;
  onTypeChange: (value: ThreadV4NotebookEntryType) => void;
  onSourceTypeChange: (value: ThreadV4SpoolSourceType) => void;
  onSourceUriChange: (value: string) => void;
  onVisibilityChange: (value: ThreadV4SpoolVisibility) => void;
  onAdd: () => void;
}

export interface ThreadV4WidgetProps extends NotebookControlProps {
  widget: ThreadV4WidgetSpec;
  data: V3WorkbenchData;
  selectedRowId?: string | null;
  onSelectRow?: (rowId: string) => void;
  projectId?: string;
  persistedProject?: boolean;
  onDockWidget?: (widgetId: string, dock: ThreadV4WidgetDock) => void;
  onToggleWidgetLock?: (widgetId: string) => void;
}

export function selectedLineageNodeIds(data: V3WorkbenchData, selectedRowId?: string | null): Set<string> {
  const ids = new Set<string>();
  if (!selectedRowId) return ids;
  const row = data.rows.find((candidate) => rowKey(candidate) === selectedRowId || candidate.runId === selectedRowId);
  if (row?.rowNodeId) ids.add(row.rowNodeId);
  for (const nodeId of Object.values(row?.cellNodeIds ?? {})) {
    if (nodeId) ids.add(nodeId);
  }
  const projectionRow = data.provenance.rows.find((candidate) => candidate.runId === row?.runId || candidate.runId === selectedRowId);
  for (const nodeId of projectionRow?.sourceNodeIds ?? []) {
    if (nodeId) ids.add(nodeId);
  }
  return ids;
}

function isThreadColumn(column: ThreadColumn | undefined): column is ThreadColumn {
  return Boolean(column);
}

export function rowState(row: ThreadRow): WidgetState | "fresh" | "warn" {
  const raw = String(row.state ?? row.status ?? "").toLowerCase();
  if (raw.includes("running") || raw.includes("pending")) return "running";
  if (raw.includes("stale")) return "stale";
  if (raw.includes("warn")) return "warn";
  return "fresh";
}

export function V4Widget({ kind, kindLabel, title, meta, state = "ok", actions, children, className = "" }: WidgetShellProps) {
  const widgetClass = ["v4-widget", className, state === "stale" && "is-stale", state === "error" && "is-error"].filter(Boolean).join(" ");
  return (
    <section className={widgetClass} data-kind={kind}>
      <header className="v4-wh">
        <span className="v4-wh-kind"><span className="dot" />{kindLabel || kind}</span>
        <span className="v4-wh-title">
          <span className="v4-wh-title-text">{title}</span>
          {meta ? <span className="v4-wh-meta">· {meta}</span> : null}
          {state !== "ok" ? <span className={`v4-badge is-${state}`}><span className="dot" />{state}</span> : null}
        </span>
        <span className="v4-wh-actions">{actions || <DefaultWidgetActions />}</span>
      </header>
      <div className="v4-wbody">{children}</div>
    </section>
  );
}

export function DefaultWidgetActions() {
  return (
    <>
      <button className="v4-wh-iconbtn" type="button" title="Refresh"><Icons.Refresh size={13} /></button>
      <button className="v4-wh-iconbtn" type="button" title="Pin selection"><Icons.Pin size={13} /></button>
      <button className="v4-wh-iconbtn" type="button" title="Float"><Icons.Float size={13} /></button>
      <button className="v4-wh-iconbtn" type="button" title="Maximize"><Icons.Maximize size={13} /></button>
      <button className="v4-wh-iconbtn" type="button" title="More"><Icons.MoreV size={13} /></button>
    </>
  );
}

export function TableWidget({ data, selectedRowId, onSelectRow, title = "Runs", compact = false }: { data: V3WorkbenchData; selectedRowId?: string | null; onSelectRow?: (rowId: string) => void; title?: string; compact?: boolean }) {
  const columns = data.columns.length ? data.columns : [{ id: "runId", label: "Run", kind: "id" as const }];
  const groups = useMemo(() => {
    const map = new Map<string, ThreadRow[]>();
    for (const row of data.rows) {
      const key = String(row.family || "runs");
      map.set(key, [...(map.get(key) || []), row]);
    }
    return [...map.entries()];
  }, [data.rows]);
  const visible = [
    columns.find((column) => column.id === "phase"),
    columns.find((column) => column.id === "geometry") || columns.find((column) => column.kind === "condition"),
    columns.find((column) => column.id === "gap_fraction"),
    columns.find((column) => column.id === "htail_z"),
    columns.find((column) => column.id === "alpha"),
    columns.find((column) => column.id === "theta_ht"),
    columns.find((column) => column.id === "T_mult"),
    columns.find((column) => column.id === "mesh") || columns.find((column) => column.kind === "metadata"),
  ].filter(isThreadColumn);
  return (
    <V4Widget kind="table" kindLabel="Table" title={title} meta={`${data.totalRowCount || data.rows.length} rows · ${columns.length} cols`}>
      <div className="v4-table-toolbar">
        <span>Group rows by</span>
        <div className="v4-group-drop">
          <span className="v4-group-pill is-pivot"><Icons.Drag size={12} />family</span>
          <span className="v4-group-pill is-shadow"><Icons.Plus size={11} />phase</span>
        </div>
        <div className="v4-table-tools">
          <div className="v4-search"><Icons.Search size={13} /><input className="v4-input is-sm" placeholder="filter..." /></div>
          <button className="v4-btn is-sm" type="button"><Icons.Filter size={13} />Filter</button>
        </div>
      </div>
      <div className="v4-table">
        <div className={`v4-thead ${compact ? "is-wide" : ""}`}>
          <div />
          {visible.map((column) => <div key={column.id} className={column.kind === "condition" ? "kind-condition" : "kind-meta"}>{column.label}</div>)}
          <div className="kind-meta">State</div>
        </div>
        <div className="v4-tbody">
          {groups.map(([family, rows]) => (
            <div className="v4-tgroup" key={family}>
              <div className={`v4-trow is-group ${compact ? "is-wide" : ""}`}>
                <div><Icons.ChevronDown size={12} /></div>
                <div className="v4-tcell-name">{data.families[family]?.label || family} <span className="v4-tcell-count">({rows.length})</span></div>
                {visible.slice(1).map((column) => <div key={column.id}>{column.id === "gap_fraction" ? formatValue(rows[0]?.[column.id]) : "—"}</div>)}
                <div>—</div>
              </div>
              {rows.map((row) => {
                const state = rowState(row);
                return (
                  <button
                    className={`v4-trow ${compact ? "is-wide" : ""} ${selectedRowId === rowKey(row) ? "is-selected" : ""}`}
                    key={rowKey(row)}
                    type="button"
                    onClick={() => onSelectRow?.(rowKey(row))}
                  >
                    <div />
                    {visible.map((column) => <div key={column.id} className={column.kind === "condition" ? "v4-tcell-pivot" : "v4-trunc"}>{formatValue(row[column.id])}</div>)}
                    <div className={`v4-tcell-${state}`}>{state === "running" ? "▶ running" : state === "warn" ? "▲ refresh" : state === "stale" ? "✕ stale" : "● fresh"}</div>
                  </button>
                );
              })}
            </div>
          ))}
          {!data.rows.length ? <div className="v4-empty"><strong>No rows loaded</strong><small>Run data will appear here when the project projection loads.</small></div> : null}
        </div>
        <footer className="v4-tfoot">
          <span>{data.rows.length} rows</span><span className="sep" /><span>{columns.filter((column) => column.kind === "condition").length} conditions</span>
          <span className="sep" /><span>{columns.filter((column) => column.kind === "quantity").length} quantities</span><span className="grow" /><span><Icons.Link size={11} /> rows -&gt; DAG</span>
        </footer>
      </div>
    </V4Widget>
  );
}

export function CorrelogramWidget({ data, title = "Correlogram" }: { data: V3WorkbenchData; title?: string }) {
  const columns = useMemo(() => {
    const numeric = data.columns.filter((column) => column.kind === "quantity" && data.rows.some((row) => typeof row[column.id] === "number"));
    return (numeric.length ? numeric : data.columns.filter((column) => data.rows.some((row) => typeof row[column.id] === "number"))).slice(0, 8);
  }, [data.columns, data.rows]);
  const matrix = useMemo(() => columns.map((xColumn) => columns.map((yColumn) => correlation(data.rows, xColumn.id, yColumn.id))), [columns, data.rows]);
  return (
    <V4Widget kind="correlogram" kindLabel="Corr" title={title} meta={`${columns.length} numeric fields`}>
      {columns.length ? (
        <div className="v4-correlogram">
          <div className="v4-corr-grid" style={{ gridTemplateColumns: `104px repeat(${columns.length}, minmax(44px, 1fr))` }}>
            <div className="v4-corr-corner">field</div>
            {columns.map((column) => <div className="v4-corr-head" key={`x-${column.id}`}>{column.label}</div>)}
            {columns.map((rowColumn, y) => (
              <div className="v4-corr-row" key={rowColumn.id} style={{ display: "contents" }}>
                <div className="v4-corr-label">{rowColumn.label}</div>
                {columns.map((column, x) => {
                  const value = matrix[y]?.[x] ?? 0;
                  const strength = Math.min(1, Math.abs(value));
                  const rgb = value < 0 ? "94, 200, 224" : "224, 164, 88";
                  return (
                    <div
                      className="v4-corr-cell"
                      key={`${rowColumn.id}-${column.id}`}
                      style={{ backgroundColor: `rgba(${rgb}, ${0.1 + strength * 0.62})`, borderColor: `rgba(${rgb}, ${0.22 + strength * 0.52})` }}
                      title={`${rowColumn.label} / ${column.label}: ${value.toFixed(2)}`}
                    >
                      {value.toFixed(2)}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="v4-corr-legend"><span>negative</span><i /><span>positive</span></div>
        </div>
      ) : (
        <EmptyState icon={<Icons.Plot size={16} />} title="No numeric fields" body="Quantity columns will appear here as a correlation grid." />
      )}
    </V4Widget>
  );
}

function correlation(rows: ThreadRow[], xKey: string, yKey: string): number {
  const pairs = rows
    .map((row) => ({ x: row[xKey], y: row[yKey] }))
    .filter((point): point is { x: number; y: number } => typeof point.x === "number" && typeof point.y === "number");
  if (pairs.length < 2) return xKey === yKey ? 1 : 0;
  const meanX = pairs.reduce((sum, point) => sum + point.x, 0) / pairs.length;
  const meanY = pairs.reduce((sum, point) => sum + point.y, 0) / pairs.length;
  let numerator = 0;
  let xVariance = 0;
  let yVariance = 0;
  for (const point of pairs) {
    const dx = point.x - meanX;
    const dy = point.y - meanY;
    numerator += dx * dy;
    xVariance += dx * dx;
    yVariance += dy * dy;
  }
  const denominator = Math.sqrt(xVariance * yVariance);
  return denominator ? Math.max(-1, Math.min(1, numerator / denominator)) : xKey === yKey ? 1 : 0;
}

export function DagWidget({ data, title = "Provenance", meta, selectedRowId }: { data: V3WorkbenchData; title?: string; meta?: string; selectedRowId?: string | null }) {
  const clusters = data.provenance.clusters.slice(0, 8);
  const nodes = data.provenance.nodes.slice(0, 18);
  const selectedNodes = useMemo(() => selectedLineageNodeIds(data, selectedRowId), [data, selectedRowId]);
  const width = 900;
  const height = 240;
  const xFor = (x: number | undefined, fallback: number) => Math.max(50, Math.min(width - 50, (x ?? fallback) + 60));
  const yFor = (y: number | undefined, fallback: number) => Math.max(42, Math.min(height - 42, (y ?? fallback) + 40));
  const nodeById = new Map(nodes.map((node, index) => [node.id, { x: xFor(node.position?.x, 80 + index * 40), y: yFor(node.position?.y, 70 + (index % 4) * 38) }]));
  return (
    <V4Widget kind="dag" kindLabel="DAG" title={title} meta={meta || `${data.provenance.representedScale.totalNodes} nodes · ${data.provenance.representedScale.totalEdges} edges`}>
      <div className="v4-dag">
        <div className="v4-dag-controls">
          <button className="v4-btn is-sm" type="button"><Icons.Sparkle size={12} />Trace</button>
          <button className="v4-btn is-sm is-icon" type="button"><Icons.Plus size={12} /></button>
          <button className="v4-btn is-sm is-icon" type="button"><Icons.Reset size={12} /></button>
        </div>
        <svg className="v4-dag-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
          {clusters.map((cluster, index) => (
            <g key={cluster.id} className="v4-dag-node is-cluster">
              <circle cx={xFor(cluster.position?.x, 70 + index * 115)} cy={yFor(cluster.position?.y, 80 + (index % 2) * 60)} r={Math.max(20, Math.min(46, cluster.radius || 28))} />
              <text className="v4-dag-label" x={xFor(cluster.position?.x, 70 + index * 115)} y={yFor(cluster.position?.y, 80 + (index % 2) * 60) + 50}>{cluster.label}</text>
            </g>
          ))}
          {data.provenance.edges.slice(0, 28).map((edge) => {
            const from = nodeById.get(edge.from);
            const to = nodeById.get(edge.to);
            if (!from || !to) return null;
            const mid = (from.x + to.x) / 2;
            const lit = selectedNodes.size === 0 || selectedNodes.has(edge.from) || selectedNodes.has(edge.to);
            return <path key={edge.id} d={`M${from.x} ${from.y} Q${mid} ${(from.y + to.y) / 2 - 24} ${to.x} ${to.y}`} className={`v4-dag-edge ${lit ? "is-lit" : ""}`} />;
          })}
          {nodes.map((node, index) => {
            const p = nodeById.get(node.id) || { x: 80 + index * 40, y: 100 };
            const selected = selectedNodes.size > 0 ? selectedNodes.has(node.id) : index === 0;
            return (
              <g key={node.id} className={`v4-dag-node ${selected ? "is-selected" : ""} ${node.health === "stale" || node.health === "warn" ? "is-stale" : ""}`}>
                <circle cx={p.x} cy={p.y} r={6} />
                <text x={p.x} y={p.y - 12}>{node.label}</text>
              </g>
            );
          })}
        </svg>
        <div className="v4-dag-legend"><span><i /> selected lineage</span><span><i /> compatible</span></div>
      </div>
    </V4Widget>
  );
}

export function GeometryWidget({ data, title = "Geometry", meta = "source CSM", projectName }: { data: V3WorkbenchData; title?: string; meta?: string; projectName?: string }) {
  const geometry = data.provenance.nodes.filter((node) => node.type === "geometry").slice(0, 2).map((node) => node.label).join(" · ");
  return (
    <V4Widget kind="geometry" kindLabel="Geometry" title={title} meta={geometry || meta}>
      <div className="v4-viewer">
        <div className="v4-viewer-toolbar"><button className="v4-btn is-sm" type="button"><Icons.Eye size={12} />wireframe</button></div>
        <svg viewBox="0 0 600 360" preserveAspectRatio="xMidYMid meet">
          <defs><linearGradient id="v4-csm-shade" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#1c3a2c" /><stop offset="1" stopColor="#0b1614" /></linearGradient></defs>
          <g transform="translate(300 195)">
            <ellipse cx="0" cy="0" rx="90" ry="14" fill="url(#v4-csm-shade)" stroke="#62c39a" strokeWidth="1" />
            <path d="M-100 -10 L100 -10 L150 12 L130 22 L-130 22 L-150 12 Z" fill="url(#v4-csm-shade)" stroke="#62c39a" strokeWidth="1" />
            <path d="M55 -4 L115 -4 L125 4 L120 8 L50 8 L45 4 Z" fill="url(#v4-csm-shade)" stroke="#62c39a" strokeWidth="1" opacity=".85" />
            {Array.from({ length: 8 }, (_, i) => <line key={i} x1={-130 + i * 30} y1={-10} x2={-120 + i * 30} y2={22} stroke="#62c39a" strokeWidth=".4" opacity=".5" />)}
          </g>
        </svg>
        <ViewerStats title={projectName || data.project?.name || "Thread project"} lines={["span 2.4 m · chord 0.36", "continuous / gapped variants"]} />
      </div>
    </V4Widget>
  );
}

export function MeshWidget({ data, title = "Mesh", meta = "legacy-beta" }: { data: V3WorkbenchData; title?: string; meta?: string }) {
  const meshes = Array.from(new Set(data.rows.map((row) => row.mesh).filter(Boolean))).join(" · ");
  return (
    <V4Widget kind="mesh" kindLabel="Mesh" title={title} meta={meshes || meta} state="stale">
      <div className="v4-viewer">
        <div className="v4-viewer-toolbar"><div className="v4-seg"><button className="is-active" type="button">surface</button><button type="button">slice</button><button type="button">vol</button></div></div>
        <svg viewBox="0 0 600 360" preserveAspectRatio="xMidYMid meet">
          <g transform="translate(300 180)">
            {Array.from({ length: 9 }, (_, i) => <ellipse key={i} cx="0" cy="0" rx={30 + i * 28} ry={10 + i * 9} fill="none" stroke={`rgba(94,200,224,${0.32 + (8 - i) * 0.05})`} strokeWidth={i === 0 ? 1.6 : 0.9} />)}
            {Array.from({ length: 24 }, (_, i) => {
              const angle = (i / 24) * Math.PI * 2;
              return <line key={i} x1={Math.cos(angle) * 30} y1={Math.sin(angle) * 11} x2={Math.cos(angle) * 280} y2={Math.sin(angle) * 100} stroke="rgba(94,200,224,0.18)" strokeWidth=".6" />;
            })}
            <ellipse cx="0" cy="0" rx="30" ry="11" fill="#0b1814" stroke="#62c39a" strokeWidth="1.4" />
          </g>
        </svg>
        <ViewerStats title={meshes || "mesh recipe"} lines={["surface / volume mesh lineage", "recipe regen pending"]} warn />
      </div>
    </V4Widget>
  );
}

export function FlowfieldWidget({ data, title = "Flowfield", meta = "Cp · slice y=0" }: { data: V3WorkbenchData; title?: string; meta?: string }) {
  const uvfCount = data.assets.filter((asset) => asset.type === "uvf").length;
  return (
    <V4Widget kind="flowfield" kindLabel="Flowfield" title={title} meta={meta} state="running" actions={<><div className="v4-seg"><button className="is-active" type="button">Cp</button><button type="button">u</button><button type="button">k</button></div><DefaultWidgetActions /></>}>
      <div className="v4-viewer">
        <svg viewBox="0 0 600 360" preserveAspectRatio="xMidYMid meet">
          <defs>
            <radialGradient id="v4-ff-hot" cx="0.35" cy="0.45" r="0.35"><stop offset="0" stopColor="#e07a6a" stopOpacity=".6" /><stop offset=".55" stopColor="#e0a458" stopOpacity=".28" /><stop offset="1" stopColor="#0d1117" stopOpacity="0" /></radialGradient>
            <radialGradient id="v4-ff-cold" cx="0.62" cy="0.55" r="0.36"><stop offset="0" stopColor="#5ec8e0" stopOpacity=".55" /><stop offset=".65" stopColor="#7fb7ff" stopOpacity=".18" /><stop offset="1" stopColor="#0d1117" stopOpacity="0" /></radialGradient>
            <linearGradient id="v4-colorbar" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#e07a6a" /><stop offset=".5" stopColor="#e0a458" /><stop offset=".75" stopColor="#62c39a" /><stop offset="1" stopColor="#5ec8e0" /></linearGradient>
          </defs>
          <rect width="600" height="360" fill="url(#v4-ff-hot)" /><rect width="600" height="360" fill="url(#v4-ff-cold)" />
          {Array.from({ length: 14 }, (_, i) => <path key={i} d={`M0 ${30 + i * 22} C150 ${30 + i * 22 + (i - 7) * 3}, 280 ${30 + i * 22 + (i - 7) * 2}, 600 ${30 + i * 22 + (i - 7) * 5}`} fill="none" stroke="rgba(230,236,242,0.16)" strokeWidth=".6" />)}
          <g transform="translate(220 180)"><path d="M-90 0 Q-50 -18, 0 -14 Q60 -8, 90 0 Q60 8, 0 14 Q-50 18, -90 0 Z" fill="#050709" stroke="#62c39a" strokeWidth="1" /><path d="M40 -2 L100 -8 L120 -4 L100 4 L40 4 Z" fill="#050709" stroke="#62c39a" strokeWidth="1" /></g>
          <g transform="translate(540 30)"><rect width="14" height="240" fill="url(#v4-colorbar)" /><text x="20" y="6">+0.8</text><text x="20" y="124">0</text><text x="20" y="244">-1.6</text></g>
        </svg>
        <ViewerStats title={uvfCount ? `${uvfCount} UVF assets` : "flowfield pending"} lines={["alpha / Mach linked from selection", "running · iter 870 / target 1500"]} />
      </div>
    </V4Widget>
  );
}

function ViewerStats({ title, lines, warn = false }: { title: string; lines: string[]; warn?: boolean }) {
  return <div className="v4-viewer-stats"><div><strong>{title}</strong></div>{lines.map((line) => <div key={line} className={warn ? "warn" : ""}>{line}</div>)}</div>;
}

export function PlotWidget({ data, title = "Plot · switcher", initialQuantity = "CL", showTabs = true }: { data: V3WorkbenchData; title?: string; initialQuantity?: string; showTabs?: boolean }) {
  const quantities = data.columns.filter((column) => column.kind === "quantity").map((column) => column.id);
  const ids = quantities.length ? quantities.slice(0, 4) : ["CL", "CD", "Cm", "residual"];
  const fallbackQuantity = ids[0] ?? initialQuantity;
  const [active, setActive] = useState(ids.includes(initialQuantity) ? initialQuantity : fallbackQuantity);
  const plot = useMemo(() => makePlot(data, active), [active, data]);
  return (
    <V4Widget kind="plot" kindLabel="Plot" title={title} meta={plot.label}>
      <div className="v4-plot">
        {showTabs ? <div className="v4-plot-tabs">{ids.map((id) => <button key={id} className={`v4-plot-tab ${id === active ? "is-active" : ""}`} type="button" onClick={() => setActive(id)}>{id}</button>)}<span className="grow" /><button className="v4-plot-tab" type="button"><Icons.Plus size={11} />Add</button></div> : null}
        <div className="v4-plot-canvas"><PlotSvg plot={plot} /></div>
      </div>
    </V4Widget>
  );
}

function makePlot(data: V3WorkbenchData, quantity: string) {
  const rows = data.rows.filter((row) => typeof row.alpha === "number" && typeof row[quantity] === "number");
  const byFamily = new Map<string, ThreadRow[]>();
  for (const row of rows) byFamily.set(String(row.family || "runs"), [...(byFamily.get(String(row.family || "runs")) || []), row]);
  const colors = ["#62c39a", "#e0a458", "#b69aff", "#5ec8e0"];
  const series = [...byFamily.entries()].slice(0, 4).map(([family, familyRows], index) => ({
    id: family,
    label: family,
    color: colors[index % colors.length],
    points: familyRows.sort((a, b) => Number(a.alpha) - Number(b.alpha)).map((row) => ({ x: Number(row.alpha), y: Number(row[quantity]) })),
  })).filter((item) => item.points.length);
  if (series.length) return { label: `${quantity} vs alpha`, xLabel: "alpha", yLabel: quantity, series };
  return {
    label: `${quantity} vs alpha`,
    xLabel: "alpha",
    yLabel: quantity,
    series: [
      { id: "continuous", label: "continuous", color: "#62c39a", points: [0, 2, 4, 6, 8, 10, 12].map((x) => ({ x, y: 0.05 + x * 0.12 - Math.max(0, x - 10) * 0.1 })) },
      { id: "gapped", label: "gapped", color: "#e0a458", points: [0, 2, 4, 6, 8, 10, 12].map((x) => ({ x, y: 0.08 + x * 0.13 - Math.max(0, x - 10) * 0.08 })) },
    ],
  };
}

function PlotSvg({ plot }: { plot: ReturnType<typeof makePlot> }) {
  const all = plot.series.flatMap((series) => series.points);
  const xmin = Math.min(...all.map((point) => point.x), 0);
  const xmax = Math.max(...all.map((point) => point.x), 1);
  const ymin = Math.min(...all.map((point) => point.y), 0);
  const ymax = Math.max(...all.map((point) => point.y), 1);
  const W = 620, H = 280, PL = 46, PR = 16, PT = 16, PB = 32;
  const sx = (x: number) => PL + ((x - xmin) / Math.max(1e-9, xmax - xmin)) * (W - PL - PR);
  const sy = (y: number) => PT + (1 - (y - ymin) / Math.max(1e-9, ymax - ymin)) * (H - PT - PB);
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        {Array.from({ length: 5 }, (_, i) => <line key={`gy${i}`} x1={PL} x2={W - PR} y1={PT + i * ((H - PT - PB) / 4)} y2={PT + i * ((H - PT - PB) / 4)} className="v4-plot-grid" />)}
        {Array.from({ length: 6 }, (_, i) => <line key={`gx${i}`} x1={PL + i * ((W - PL - PR) / 5)} x2={PL + i * ((W - PL - PR) / 5)} y1={PT} y2={H - PB} className="v4-plot-grid" />)}
        <line x1={PL} y1={PT} x2={PL} y2={H - PB} className="v4-plot-axis" /><line x1={PL} y1={H - PB} x2={W - PR} y2={H - PB} className="v4-plot-axis" />
        <text x="4" y={PT - 4} className="v4-plot-label">{plot.yLabel}</text><text x={W - PR} y={H - 4} className="v4-plot-label" textAnchor="end">{plot.xLabel}</text>
        {plot.series.map((series) => {
          const d = series.points.map((point, index) => `${index === 0 ? "M" : "L"} ${sx(point.x)} ${sy(point.y)}`).join(" ");
          return <path key={series.id} d={d} className="v4-plot-series-line" stroke={series.color} />;
        })}
      </svg>
      <div className="v4-plot-legend">{plot.series.map((series) => <span key={series.id}><i style={{ background: series.color }} />{series.label}</span>)}</div>
    </>
  );
}

export function AssetRailWidget({ data, title = "Saved plots", types }: { data: V3WorkbenchData; title?: string; types?: string[] }) {
  const assets = types ? data.assets.filter((asset) => types.includes(asset.type)) : data.assets;
  return (
    <V4Widget kind="plot" kindLabel="Assets" title={title} meta={`${assets.length} assets`}>
      <div className="v4-asset-list">{assets.slice(0, 8).map((asset) => <AssetRow key={asset.id} asset={asset} />)}{!assets.length ? <EmptyState icon={<Icons.SavedPlots size={16} />} title="No assets yet" body="Pin a plot or artifact to save it for the project." /> : null}</div>
    </V4Widget>
  );
}

function AssetRow({ asset }: { asset: ThreadAsset }) {
  return (
    <button className="v4-asset" type="button">
      <span className="v4-asset-icon" data-type={asset.type}>{asset.type === "uvf" ? <Icons.Flowfield size={16} /> : asset.type === "plot" ? <Icons.Plot size={16} /> : <Icons.Asset size={16} />}</span>
      <span className="v4-asset-main"><strong>{asset.label}</strong><small>{asset.source} · {asset.updated}</small><p>{asset.summary || asset.sub}</p><span className="v4-asset-refs"><span>{asset.refs.rows?.length ?? 0} rows</span><span>{asset.refs.cells?.length ?? 0} cells</span></span></span>
      <span className="v4-asset-side"><span className={`v4-badge is-${asset.health === "warn" ? "stale" : asset.health}`}><span className="dot" />{asset.health}</span><Icons.ChevronRight size={14} /></span>
    </button>
  );
}

export function BuildWidget({ data }: { data: V3WorkbenchData }) {
  const [campaign, setCampaign] = useState("continuous-cruise");
  const [alpha, setAlpha] = useState("7");
  const [family = "continuous", phase = "cruise"] = campaign.split("-");
  return (
    <V4Widget kind="build" kindLabel="Build" title="Tsangpo build" meta="build_surface.v1">
      <div className="v4-build">
        <div><div className="v4-build-eyebrow">Build · Tsangpo geometry</div><h3>{family} · {phase}</h3><div className="v4-build-meta"><span>{data.rows.length || 8} current rows</span><span>31 planned cases</span></div></div>
        <Segment label="Family" values={["continuous", "gapped"]} active={family} onPick={(value) => setCampaign(`${value}-${phase}`)} />
        <Segment label="Phase" values={["cruise", "takeoff", "landing"]} active={phase} onPick={(value) => setCampaign(`${family}-${value}`)} />
        <div className="v4-numfields"><label className="v4-field"><span>alpha</span><input className="v4-input" value={alpha} onChange={(event) => setAlpha(event.currentTarget.value)} /></label><label className="v4-field"><span>theta_ht</span><input className="v4-input" defaultValue="0" /></label><label className="v4-field"><span>T_mult</span><input className="v4-input" defaultValue="1" /></label></div>
        <div className="v4-sweep-row"><button className="v4-sweep-pill is-active" type="button">alpha sweep · 10</button><button className="v4-sweep-pill" type="button">theta_ht sweep · 10</button><button className="v4-sweep-pill" type="button"><Icons.Plus size={10} />add</button></div>
        <dl className="v4-build-plan"><dt>plan</dt><dd>Thread-owned Tsangpo campaign</dd><dt>cases</dt><dd>1 baseline + 30 sweep</dd><dt>upstream</dt><dd>tsangpo.csm -&gt; legacy-beta mesh -&gt; solver</dd></dl>
        <div className="v4-repo"><div className="v4-build-eyebrow">Generating script</div><div className="v4-repo-row"><Icons.Github size={13} /><code>qiqi/tsangpo</code><span className="grow" /><span className="v4-repo-sha">d889d19a</span></div></div>
        <div className="v4-build-actions"><button className="v4-btn is-brand" type="button"><Icons.Sparkle size={13} />Plan in Thread</button><button className="v4-btn is-mutate" type="button"><Icons.Cmd size={12} />Submit to Flow360</button></div>
      </div>
    </V4Widget>
  );
}

function Segment({ label, values, active, onPick }: { label: string; values: string[]; active: string; onPick: (value: string) => void }) {
  return <div className="v4-field"><span>{label}</span><div className="v4-seg">{values.map((value) => <button key={value} type="button" className={active === value ? "is-active" : ""} onClick={() => onPick(value)}>{value}</button>)}</div></div>;
}

export function PivotSummaryWidget({ data }: { data: V3WorkbenchData }) {
  const pivot = data.pivotViews[0];
  return (
    <V4Widget kind="pivot" kindLabel="Pivot" title="Pivot view" meta={pivot?.label || "alpha sweep"}>
      <div className="v4-pivot-body"><span className="v4-field-label">holding</span><div className="v4-chip-wrap">{(pivot?.heldFields || []).slice(0, 6).map((field) => <span key={field.id} className="v4-chip is-pivot">{field.label} = {field.value}</span>)}{!pivot?.heldFields.length ? <span className="v4-chip is-pivot">family = continuous</span> : null}</div><span className="v4-field-label">sweeping</span><span className="v4-chip is-brand"><Icons.Sparkle size={10} />{pivot?.sweepField?.label || "alpha"} · compatible rows</span><p>{pivot?.compatibleRowIds.length || data.rows.length} rows are compatible with this pivot.</p></div>
    </V4Widget>
  );
}

export function NotebookWidget(props: NotebookControlProps) {
  return (
    <V4Widget kind="notebook" kindLabel="Spool" title="Notebook" meta="people · agents · sources">
      <div className="v4-notebook-widget">
        <div className="v4-notebook-composer">
          <select className="v4-input v4-select" value={props.type} onChange={(event) => props.onTypeChange(event.currentTarget.value as ThreadV4NotebookEntryType)}><option value="note">Person</option><option value="agent">Agent</option><option value="decision">Decision</option><option value="task">Task</option></select>
          <select className="v4-input v4-select" value={props.sourceType} onChange={(event) => props.onSourceTypeChange(event.currentTarget.value as ThreadV4SpoolSourceType)}><option value="manual">Manual</option><option value="slack">Slack</option><option value="gmail">Gmail</option><option value="drive">Drive</option><option value="flow360">Flow360</option><option value="github">GitHub</option></select>
          <input className="v4-input" value={props.sourceUri} onChange={(event) => props.onSourceUriChange(event.currentTarget.value)} placeholder="source URI" />
          <textarea className="v4-input v4-textarea" value={props.draft} onChange={(event) => props.onDraftChange(event.currentTarget.value)} placeholder="Capture a note, agent observation, decision, or task" />
          <button className="v4-btn is-solid" type="button" onClick={props.onAdd}>Add to Spool</button>
        </div>
        <div className="v4-notebook-entries">
          {props.entries.slice(0, 8).map((entry) => <article key={entry.id} className={`v4-note is-${entry.entry_type}`}><header><span>{entry.author_label || entry.entry_type} · {entry.source_type}</span><small>{entry.created_at ? new Date(entry.created_at).toLocaleString() : "local"}</small></header><p>{entry.body}</p>{entry.source_uri ? <a href={entry.source_uri}>{entry.source_uri}</a> : null}</article>)}
          {!props.entries.length ? <EmptyState icon={<Icons.Comments size={16} />} title="The shared notebook is ready" body="Notes from people, agents, Slack, Gmail, Flow360, and GitHub land here." /> : null}
        </div>
      </div>
    </V4Widget>
  );
}

function EmptyState({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return <div className="v4-empty"><div className="v4-empty-icon">{icon}</div><strong>{title}</strong><small>{body}</small></div>;
}

function RealPanelFrame({ kind, children }: { kind: string; children: ReactNode }) {
  return <div className={`v4-real-panel is-${kind}`}>{children}</div>;
}

export function ThreadV4Widget({ widget, data, selectedRowId, onSelectRow, projectId, persistedProject, onDockWidget, onToggleWidgetLock, ...notebook }: ThreadV4WidgetProps) {
  const dock = widget.position.dock ?? "canvas";
  const locked = widget.bindings?.locked === true || widget.state?.locked === true;
  const realProject = persistedProject && projectId;
  return (
    <div className={`v4-layout-widget dock-${dock} ${locked ? "is-locked" : ""}`} style={{ gridColumn: `${widget.position.x + 1} / span ${widget.position.w}`, gridRow: `${widget.position.y + 1} / span ${widget.position.h}` }}>
      {widget.kind === "table" && realProject ? <RealPanelFrame kind="data"><DataExplorerPanel projectId={projectId} isVisible /></RealPanelFrame> : null}
      {widget.kind === "table" && !realProject ? <TableWidget data={data} selectedRowId={selectedRowId} onSelectRow={onSelectRow} /> : null}
      {widget.kind === "dag" && realProject ? <RealPanelFrame kind="dag"><DAGPanel projectId={projectId} /></RealPanelFrame> : null}
      {widget.kind === "dag" && !realProject ? <DagWidget data={data} selectedRowId={selectedRowId} /> : null}
      {widget.kind === "geometry" ? <GeometryWidget data={data} /> : null}
      {widget.kind === "mesh" ? <MeshWidget data={data} /> : null}
      {widget.kind === "flowfield" && realProject ? <RealPanelFrame kind="flowfield"><Flow360ViewerPanel projectId={projectId} branch={data.branch || "main"} rows={data.rows as Record<string, unknown>[]} entityTypes={["case"]} /></RealPanelFrame> : null}
      {widget.kind === "flowfield" && !realProject ? <FlowfieldWidget data={data} /> : null}
      {widget.kind === "plot" ? <PlotWidget data={data} /> : null}
      {widget.kind === "asset-rail" ? <AssetRailWidget data={data} /> : null}
      {widget.kind === "build-surface" ? <BuildWidget data={data} /> : null}
      {widget.kind === "notebook" ? <NotebookWidget {...notebook} /> : null}
      {widget.kind === "markdown-note" ? <V4Widget kind="md" kindLabel="Note" title={widget.title}><div className="v4-markdown-note">{String(widget.state?.body ?? "Pinned analysis note")}</div></V4Widget> : null}
      {onDockWidget || onToggleWidgetLock ? (
        <span className="v4-layout-tools">
          {onDockWidget ? (
            <button className="v4-layout-tool" type="button" disabled={locked} onClick={() => onDockWidget(widget.id, dock === "floating" ? "canvas" : "floating")}>
              {dock === "floating" ? "dock" : "float"}
            </button>
          ) : null}
          {onToggleWidgetLock ? (
            <button className="v4-layout-tool" type="button" onClick={() => onToggleWidgetLock(widget.id)} title={locked ? "Unlock widget position" : "Lock widget position"}>
              <Icons.Lock size={11} />{locked ? "locked" : "lock"}
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}
